import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Plus } from "lucide-react";
import { toast } from "sonner";
import { getProducts } from "../lib/api";
import { useCart } from "../context/CartContext";
import { BUNDLE_SNAPSHOT } from "../data/catalogSnapshot";

export const BundleFeature = () => {
  const [bundle, setBundle] = useState(BUNDLE_SNAPSHOT);
  const { addItem, cartEnabled } = useCart();

  useEffect(() => {
    let cancelled = false;
    getProducts()
      .then((data) => {
        if (cancelled || !Array.isArray(data)) return;
        const live = data.find((p) => p.is_bundle);
        if (live) setBundle(live);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  if (!bundle) return null;

  const fullPrice = Number(bundle.compare_at_price) || 0;
  const saving = fullPrice > bundle.price ? fullPrice - bundle.price : 0;
  const soldOut = bundle.stock <= 0;

  const handleAdd = () => {
    addItem(bundle);
    toast.success("Cassa Mista aggiunta al carrello");
  };

  return (
    <section className="relative bg-[#0D110D] py-16 md:py-24" data-testid="bundle-feature">
      <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-white/10 border border-white/10 overflow-hidden"
        >
          <Link to={`/birra/${bundle.id}`} className="relative block aspect-[4/3] lg:aspect-auto bg-[#141A14] overflow-hidden group" data-testid="bundle-feature-image-link">
            <img
              src={bundle.image}
              alt={bundle.name}
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-[700ms] ease-out group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0D110D]/60 via-transparent to-transparent" />
            <span className="absolute top-5 left-5 bg-[#D47738] text-[#0D110D] font-mono-accent text-[10px] uppercase tracking-[0.2em] px-4 py-2 font-semibold">
              Parti da qui — {bundle.can_count} lattine
            </span>
          </Link>

          <div className="bg-[#141A14] p-8 md:p-12 lg:p-16 flex flex-col justify-center">
            <p className="font-mono-accent text-xs uppercase tracking-[0.3em] text-[#D47738] mb-5">
              La cassa del Cinghiale
            </p>
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tight text-[#E8E2D2] mb-6">
              {bundle.name.split("—")[0].trim()}{" "}
              <span className="italic text-[#D47738]">in un clic</span>
            </h2>
            <p className="text-[#E8E2D2]/60 leading-relaxed text-base mb-8 max-w-xl">
              {bundle.description}
            </p>

            <div className="flex items-end gap-4 mb-8" data-testid="bundle-feature-price">
              <span className="font-serif-display text-4xl md:text-5xl text-[#E8E2D2]">
                € {bundle.price.toFixed(2)}
              </span>
              {saving > 0 && (
                <>
                  <span className="font-mono-accent text-lg text-[#E8E2D2]/30 line-through mb-1">
                    € {fullPrice.toFixed(2)}
                  </span>
                  <span className="font-mono-accent text-[10px] uppercase tracking-[0.15em] bg-[#4A6E4A]/25 text-[#4A6E4A] border border-[#4A6E4A]/40 px-3 py-1.5 mb-1.5" data-testid="bundle-feature-saving">
                    Risparmi € {saving.toFixed(2)}
                  </span>
                </>
              )}
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              {cartEnabled && !soldOut ? (
                <button
                  onClick={handleAdd}
                  className="inline-flex items-center justify-center gap-2 bg-[#D47738] text-[#0D110D] px-8 py-4 font-mono-accent text-xs uppercase tracking-[0.2em] hover:bg-[#E8E2D2] transition-colors duration-300"
                  data-testid="bundle-feature-add-btn"
                >
                  <Plus className="w-4 h-4" strokeWidth={2} /> Aggiungi la cassa
                </button>
              ) : soldOut ? (
                <div className="inline-flex items-center justify-center border border-white/10 px-8 py-4 font-mono-accent text-xs uppercase tracking-[0.2em] text-[#E8E2D2]/40" data-testid="bundle-feature-soldout">
                  Esaurito
                </div>
              ) : null}
              <Link
                to={`/birra/${bundle.id}`}
                className="inline-flex items-center justify-center gap-2 border border-white/20 px-8 py-4 font-mono-accent text-xs uppercase tracking-[0.2em] text-[#E8E2D2] hover:border-[#D47738] hover:text-[#D47738] transition-colors"
                data-testid="bundle-feature-detail-link"
              >
                Scopri di più <ArrowRight className="w-4 h-4" strokeWidth={2} />
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
